'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import { Button } from '@microservices/design-system';
import { WhitespaceHighlightedText } from './WhitespaceHighlightedText';

interface TruncatedTextProps {
  text: string;
  maxLines?: number;
  title?: string;
}

/**
 * Displays text with CSS line clamping and a "View full text" link
 * that opens a read-only modal when the content overflows.
 */
export function TruncatedText({ text, maxLines = 3, title = 'Full Text' }: TruncatedTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [text, maxLines]);

  const handleOpen = useCallback(() => setDialogOpen(true), []);
  const handleClose = useCallback(() => setDialogOpen(false), []);

  return (
    <>
      <Box>
        <WhitespaceHighlightedText
          ref={textRef}
          text={text}
          variant="body1"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        />
        {isOverflowing && (
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={handleOpen}
            sx={{ mt: 0.5 }}
          >
            View full text
          </Link>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <WhitespaceHighlightedText text={text} variant="body1" />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
